import React, { useEffect, useState } from 'react';
import { emailLogAPI } from '../api';

/**
 * EmailTemplatePreview — admin-only inline QA widget.
 *
 * Lets the founder render any transactional email template with sample data
 * in the configured languages, without contacting Resend. The endpoint uses a
 * ContextVar-based dry-run mode in `email_utils.send_email`, so no email_log
 * row is written and no real address is touched.
 *
 * Three controls:
 *   - Kind dropdown (10 templates)
 *   - Language dropdown (only shown when the template supports >1 language)
 *   - Inline iframe rendering the rendered HTML, plus the resolved subject
 */
const EmailTemplatePreview = () => {
  const [catalog, setCatalog] = useState([]);
  const [kind, setKind] = useState('board_update');
  const [lang, setLang] = useState('en');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState('');

  // Load the catalog once on mount.
  useEffect(() => {
    emailLogAPI.previewCatalog()
      .then(({ data }) => setCatalog(data?.kinds || []))
      .catch(() => setCatalog([]));
  }, []);

  // Keep `lang` valid whenever `kind` changes.
  useEffect(() => {
    const entry = catalog.find((k) => k.id === kind);
    if (entry && !entry.langs.includes(lang)) {
      setLang(entry.langs[0] || 'en');
    }
  }, [kind, catalog, lang]);

  // Reset send feedback whenever the user changes the selection.
  useEffect(() => {
    setSendResult(null);
    setSendError('');
  }, [kind, lang]);

  // Fetch the preview whenever kind or lang changes.
  useEffect(() => {
    if (!kind) return;
    setLoading(true);
    setError('');
    emailLogAPI.preview(kind, lang)
      .then(({ data }) => setPreview(data))
      .catch((e) => setError(e?.response?.data?.detail || 'Preview failed'))
      .finally(() => setLoading(false));
  }, [kind, lang]);

  const handleSendTest = async () => {
    setSending(true);
    setSendResult(null);
    setSendError('');
    try {
      const { data } = await emailLogAPI.sendTestToSelf(kind, lang);
      setSendResult(data);
    } catch (e) {
      setSendError(e?.response?.data?.detail || 'Failed to send test');
    } finally {
      setSending(false);
    }
  };

  const currentEntry = catalog.find((k) => k.id === kind);
  const supportedLangs = currentEntry?.langs || ['en'];
  const showLangPicker = supportedLangs.length > 1;
  const emailDoc = preview?.emails?.[0];

  return (
    <section data-testid="email-template-preview" style={{ marginTop: 56 }}>
      <div style={{ borderTop: '1px solid var(--kahla-border)', paddingTop: 28 }}>
        <p style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 11, letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: '#7a6a4a',
          margin: 0,
        }}>
          Template preview
        </p>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: 15, lineHeight: 1.7,
          color: 'var(--kahla-text-light)',
          margin: '6px 0 22px',
          maxWidth: 620,
        }}>
          Render any of Maestra's transactional emails with sample data — no
          delivery, no log entry. A quiet QA tool for catching copy issues
          before a real client sees them.
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <PreviewSelect
            label="Template"
            value={kind}
            onChange={setKind}
            options={catalog.map((k) => [k.id, k.label])}
            testId="email-preview-kind"
          />
          {showLangPicker && (
            <PreviewSelect
              label="Language"
              value={lang}
              onChange={setLang}
              options={supportedLangs.map((l) => [l, l.toUpperCase()])}
              testId="email-preview-lang"
            />
          )}
          {!showLangPicker && (
            <span style={{
              fontSize: 11, letterSpacing: '0.14em',
              color: 'var(--kahla-text-light)',
              fontStyle: 'italic',
            }}>
              English only
            </span>
          )}

          <button
            type="button"
            data-testid="email-preview-send-test"
            onClick={handleSendTest}
            disabled={sending || !emailDoc}
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: '1px solid var(--kahla-charcoal)',
              padding: '9px 22px',
              fontFamily: 'inherit',
              fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'var(--kahla-charcoal)',
              cursor: sending ? 'wait' : 'pointer',
              opacity: sending || !emailDoc ? 0.45 : 1,
              transition: 'background 160ms ease, color 160ms ease',
            }}
            title="Deliver this rendered template to your own admin email address."
          >
            {sending ? 'Sending…' : 'Send test to my inbox'}
          </button>
        </div>

        {sendResult && (
          <div
            data-testid="email-preview-send-result"
            style={{
              background: '#f3f5ee',
              border: '1px solid #d4dfc4',
              padding: '12px 18px',
              marginBottom: 18,
              display: 'flex',
              alignItems: 'baseline',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <span style={{
              fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: '#5a7a3a',
            }}>Sent</span>
            <span style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic', fontSize: 15,
              color: 'var(--kahla-charcoal)',
            }}>
              Test delivered to <strong style={{ fontStyle: 'normal' }}>{sendResult.to}</strong>.
              Check your inbox in a moment — it will also appear in the log above.
            </span>
          </div>
        )}
        {sendError && (
          <p data-testid="email-preview-send-error" style={{
            color: '#7a3a3a', background: '#f8eee9',
            padding: '10px 14px', fontSize: 13, fontStyle: 'italic',
            marginBottom: 18,
          }}>{sendError}</p>
        )}

        {loading && (
          <p style={{ fontStyle: 'italic', color: 'var(--kahla-text-light)' }}>Rendering…</p>
        )}
        {error && (
          <p data-testid="email-preview-error" style={{
            color: '#7a3a3a',
            background: '#f8eee9',
            padding: '10px 14px',
            fontSize: 13, fontStyle: 'italic',
          }}>{error}</p>
        )}

        {!loading && !error && emailDoc && (
          <div data-testid="email-preview-output">
            <div style={{
              background: '#faf8f5',
              border: '1px solid var(--kahla-border)',
              borderBottom: 'none',
              padding: '14px 22px',
            }}>
              <p style={{
                margin: 0, fontSize: 10, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: '#9b8a6c',
              }}>Subject</p>
              <p data-testid="email-preview-subject" style={{
                margin: '6px 0 0', fontFamily: 'Playfair Display, serif',
                fontSize: 17, color: 'var(--kahla-charcoal)',
              }}>{emailDoc.subject}</p>
              <p style={{
                margin: '14px 0 0', fontSize: 11, letterSpacing: '0.06em',
                color: 'var(--kahla-text-light)',
              }}>
                <span style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>To</span>
                {' '}{emailDoc.to}
                {emailDoc.reply_to ? (
                  <>{' · '}
                    <span style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>Reply-to</span>
                    {' '}{emailDoc.reply_to}
                  </>
                ) : null}
              </p>
            </div>
            <iframe
              data-testid="email-preview-iframe"
              title="Email preview"
              srcDoc={emailDoc.html}
              sandbox=""
              style={{
                width: '100%',
                minHeight: 640,
                border: '1px solid var(--kahla-border)',
                background: '#faf8f5',
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

const PreviewSelect = ({ label, value, onChange, options, testId }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <span style={{
      fontSize: 10, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--kahla-text-light)',
    }}>{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{
        padding: '7px 10px', fontFamily: 'inherit',
        fontSize: 12,
        background: '#fff', border: '1px solid var(--kahla-border)',
        color: 'var(--kahla-charcoal)',
        minWidth: 180,
      }}
    >
      {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
    </select>
  </label>
);

export default EmailTemplatePreview;
